import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import * as reducers from '../../store/reducers';
import { clearInvitedTeachers } from '../../store/users/actions';
import { fetchSeatLimitsForSchool } from '../../store/schools/actions';
import { removeSingleTeacherForm } from '../../store/forms/actions';
import { hideModal } from '../../store/ui/actions';
import { filterObjectByKeys } from '../../utilities';

import Lightbox from 'Components/Lightbox';
import SectionHeader from 'Components/SectionHeader';
import InviteMultipleTeachersForm from 'Containers/InviteMultipleTeachersForm';
import InviteSingleTeacherForm from 'Containers/InviteSingleTeacherForm';
import EmailListInput from 'Components/EmailListInput';
import FlashMessage from 'Components/FlashMessage';
import SchoolSeatsFilled from 'Components/SchoolSeatsFilled';
import ConnectedFormattedMessage from 'Containers/ConnectedFormattedMessage';

class InviteTeachers extends React.Component {
  constructor(props) {
    super(props);

    this.getFlashMessage = this.getFlashMessage.bind(this);
    this.handleLightboxClose = this.handleLightboxClose.bind(this);

    this.state = {
      readyToCloseModal: false,
    };
  }

  componentDidMount() {
    const { activeSchoolId } = this.props;
    this.props.fetchSeatLimitsForSchool(activeSchoolId, 'teachers');
  }

  componentDidUpdate() {
    const { showInviteResults, resultsByStatus } = this.props;
    // Translation: If the emails all submitted successfully,
    // close the lightbox!
    if (
      showInviteResults &&
      resultsByStatus.error.length === 0 &&
      !this.state.readyToCloseModal
    ) {
      this.setState({ readyToCloseModal: true }, () =>
        window.setTimeout(this.handleLightboxClose, 3000)
      );
    }
  }

  getFlashMessage() {
    const { resultsByStatus } = this.props;
    const messageType = resultsByStatus.error.length > 0 ? 'error' : 'success';
    const successText = (
      <ConnectedFormattedMessage
        id="invite_teachers.success_message"
        values={{
          result_count: resultsByStatus.success.length,
        }}
      />
    );
    const errorText =
      resultsByStatus.success.length > 0 ? (
        <ConnectedFormattedMessage id="invite_teachers.some_failures_message" />
      ) : (
        <ConnectedFormattedMessage id="invite_teachers.failure_message" />
      );

    return (
      <FlashMessage
        text={messageType === 'success' ? successText : errorText}
        type={messageType}
        location="lightbox-internal"
      />
    );
  }

  handleLightboxClose() {
    this.props.hideModal('invite-teachers');
    this.props.clearInvitedTeachers();
    this.setState({ readyToCloseModal: false });
  }

  render() {
    const {
      teachers,
      resultsByEmail,
      resultsByStatus,
      seatLimit,
      showInviteResults,
      isModalActive,
    } = this.props;
    return (
      <Lightbox
        visible={isModalActive}
        closeHandler={() => this.handleLightboxClose()}
        type="invite-teachers"
      >
        <div className="Lightbox__header-container">
          <SectionHeader
            title={<ConnectedFormattedMessage id="invite_teachers.title" />}
            description={
              <p className="SectionHeader__description">
                <ConnectedFormattedMessage id="invite_teachers.description" />
              </p>
            }
          />
          {teachers ? (
            <SchoolSeatsFilled used={teachers.length} total={seatLimit} />
          ) : null}
        </div>
        {showInviteResults ? this.getFlashMessage() : null}
        {showInviteResults ? (
          <div>
            {resultsByStatus.error.length ? (
              <div className="Lightbox__subheader">
                <ConnectedFormattedMessage id="invite_teachers.error_subheader" />
              </div>
            ) : null}
            {resultsByStatus.error.map((email, i) => (
              <InviteSingleTeacherForm
                key={`invite-${email}`}
                position={parseInt(i, 10) + 1}
                email={email}
                response={resultsByEmail[email]}
                removeFormHanlder={(originalEmail, formId) =>
                  this.props.removeSingleTeacherForm(originalEmail, formId)
                }
              />
            ))}
            <div className="Lightbox__subheader">
              <ConnectedFormattedMessage id="invite_teachers.sent_invites_subheader" />
            </div>
            <EmailListInput
              readonly
              handleChange={() => null}
              name="inviteList"
              value=""
              emailStatus={filterObjectByKeys(
                resultsByEmail,
                resultsByStatus.success
              )}
            />
          </div>
        ) : (
          <InviteMultipleTeachersForm />
        )}
      </Lightbox>
    );
  }
}

InviteTeachers.propTypes = {
  teachers: PropTypes.array,
  activeSchoolId: PropTypes.number,
  seatLimit: PropTypes.number,
  resultsByEmail: PropTypes.object,
  resultsByStatus: PropTypes.object,
  showInviteResults: PropTypes.bool,
  isModalActive: PropTypes.bool,
  removeSingleTeacherForm: PropTypes.func,
  fetchSeatLimitsForSchool: PropTypes.func,
  hideModal: PropTypes.func,
  clearInvitedTeachers: PropTypes.func,
};

const mapStateToProps = state => {
  const isPostingEmails = reducers.isFormPosting(
    state,
    'inviteMultipleTeachers'
  );
  const teachersInvitedCount = reducers.getTeachersInvitedCount(state);
  return {
    teachers: reducers.getTeachersForActiveSchool(state),
    activeSchoolId: reducers.getActiveSchoolId(state),
    seatLimit: reducers.getActiveSchoolSeatLimit(state, 'teachers'),
    resultsByEmail: reducers.getInvitedTeachersResults(state),
    resultsByStatus: reducers.getInvitedEmailsByResultStatus(state),
    showInviteResults: !isPostingEmails && teachersInvitedCount > 0,
    isModalActive: reducers.isModalActive(state, 'invite-teachers'),
  };
};

export default connect(mapStateToProps, {
  fetchSeatLimitsForSchool,
  removeSingleTeacherForm,
  clearInvitedTeachers,
  hideModal,
})(InviteTeachers);
